import type { CrossCheckClause, PastAgreementOption, PastAgreementValue } from '../types/crossCheck';
import { formatOoxmlParagraphForModel, markupToAcceptedPlainText } from './trackChangesModelText';

function normalizeComparableText(text: string): string {
  return (text || '').replace(/\s+/g, ' ').trim();
}

function looksLikeOoxml(text: string): boolean {
  const t = (text || '').trimStart();
  return (
    t.startsWith('<w:') ||
    t.startsWith('<pkg:') ||
    t.startsWith('<?xml') ||
    t.includes('<w:p') ||
    t.includes('<w:ins') ||
    t.includes('<w:del')
  );
}

function extractAcceptedFromXml(xml: string): string {
  try {
    return normalizeComparableText(markupToAcceptedPlainText(formatOoxmlParagraphForModel(xml)));
  } catch {
    return '';
  }
}

function acceptedTextFromValue(value: unknown): string {
  if (typeof value !== 'string') return '';
  const stripped = value.trim();
  if (!stripped) return '';
  if (looksLikeOoxml(stripped)) {
    const extracted = extractAcceptedFromXml(stripped);
    if (extracted) return extracted;
  }
  if (stripped.startsWith('{') || stripped.startsWith('[')) {
    try {
      const parsed = JSON.parse(stripped) as { edited_text?: string; text?: string; client_request?: string };
      const nested = parsed.edited_text || parsed.text || parsed.client_request || '';
      if (nested.trim()) {
        if (looksLikeOoxml(nested)) {
          const extracted = extractAcceptedFromXml(nested);
          if (extracted) return extracted;
        }
        return normalizeComparableText(nested);
      }
    } catch {
      /* fallthrough */
    }
  }
  return normalizeComparableText(stripped);
}

function clientRequestXml(clause: Pick<CrossCheckClause, 'client_request' | 'track_changes'>): string | null {
  const parts = clause.track_changes?.track_changes_parts;
  if (Array.isArray(parts) && parts.length > 0) {
    const requestPart = parts.find(
      (part: { role?: string; paragraph_xml?: string }) =>
        part && typeof part === 'object' && part.role === 'request' && part.paragraph_xml,
    );
    if (requestPart?.paragraph_xml) return requestPart.paragraph_xml;
    const firstXml = parts.find(
      (part: { paragraph_xml?: string }) =>
        part && typeof part === 'object' && typeof part.paragraph_xml === 'string' && part.paragraph_xml.trim(),
    );
    if (firstXml?.paragraph_xml) return firstXml.paragraph_xml;
  }
  const cr = clause.client_request?.trim() || '';
  if (cr && looksLikeOoxml(cr)) return cr;
  if (clause.track_changes?.paragraph_xml) return clause.track_changes.paragraph_xml;
  return null;
}

export function acceptedClientRequestText(
  clause: Pick<CrossCheckClause, 'client_request' | 'edited_text' | 'text' | 'track_changes'>,
): string {
  const xml = clientRequestXml(clause);
  if (xml) {
    const extracted = extractAcceptedFromXml(xml);
    if (extracted) return extracted;
  }
  if (clause.edited_text?.trim()) return acceptedTextFromValue(clause.edited_text);
  return acceptedTextFromValue(clause.client_request || clause.text || '');
}

export function acceptedPastAgreementText(option: Pick<PastAgreementOption, 'text'> & { paragraph_xml?: string }): string {
  if (option.paragraph_xml?.trim()) {
    const extracted = extractAcceptedFromXml(option.paragraph_xml);
    if (extracted) return extracted;
  }
  return acceptedTextFromValue(option.text || '');
}

export function coerceSimilarityPercent(value: unknown): number | null {
  if (value == null || value === '') return null;
  const number = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(number)) return null;
  const percent = number > 0 && number <= 1 ? number * 100 : number;
  return Math.max(0, Math.min(100, Math.round(percent)));
}

export function resolvePastAgreementSimilarity(
  clientText: string,
  pastText: string,
  storedSimilarity: unknown,
): number {
  const client = normalizeComparableText(clientText);
  const past = normalizeComparableText(pastText);
  const stored = coerceSimilarityPercent(storedSimilarity);
  if (client && past && client === past) {
    return stored == null || stored === 100 ? 100 : stored;
  }
  if (stored === 100 && client && past) return 99;
  return stored ?? 0;
}

export function capFalsePerfectPastAgreementSimilarities<T extends Pick<CrossCheckClause, 'past_agreement' | 'client_request' | 'edited_text' | 'text' | 'track_changes'>>(
  clause: T,
): T {
  const options = clause.past_agreement?.options;
  if (!Array.isArray(options) || options.length === 0) return clause;
  const clientAccepted = acceptedClientRequestText(clause);
  let changed = false;
  const nextOptions = options.map((option) => {
    if (coerceSimilarityPercent(option.similarity) !== 100) return option;
    const resolved = resolvePastAgreementSimilarity(
      clientAccepted,
      acceptedPastAgreementText(option),
      option.similarity,
    );
    if (resolved === option.similarity) return option;
    changed = true;
    return { ...option, similarity: resolved };
  });
  if (!changed) return clause;
  const nextPast: PastAgreementValue = { ...clause.past_agreement!, options: nextOptions };
  return { ...clause, past_agreement: nextPast };
}
